import { useEffect, useState } from "react";
import { useAppSelector } from "../store/hooks";
import { formatAmount } from "./format";
import { getDollarExchangeRate } from "../services/currencyServices";

export type Currency = "USD" | "BS";
export type CurrencyExchange = {
  symbol: string;
  exchange: number;
};
export type CurrencyExchanges = {
  [key in Currency]: CurrencyExchange;
};

export const useCurrencyExchanges = (date?: string) => {
  const dollarExchange = 1;

  // Live BS rate from Redux, read reactively on every render. Callers that do
  // not pass a date must stay in sync when the rate lands in the store later
  // (App.tsx fetches it asynchronously at startup, and redux-persist rehydrates
  // it on reload). Seeding this into useState once would freeze it at mount and
  // produce stale/NaN conversions (e.g. the DHL "Flete Envío" amount).
  const reduxBsExchange = useAppSelector(
    (state) => state.inmutable.exchangeRate
  ).exchangeRate;

  // Date-specific historical rate, fetched on demand. Overrides the live rate
  // only for callers that pass a date.
  const [fetchedBsExchange, setFetchedBsExchange] = useState<
    number | undefined
  >(undefined);

  useEffect(() => {
    if (!date) {
      return;
    }

    const afunction = async () => {
      const response = await getDollarExchangeRate(date);

      if (response && response.model) {
        setFetchedBsExchange(response.model);
      }
    };

    afunction();
  }, [date]);

  const bsExchange = date
    ? fetchedBsExchange ?? reduxBsExchange
    : reduxBsExchange;

  const result: CurrencyExchanges = {
    USD: {
      symbol: "$",
      exchange: dollarExchange,
    },
    BS: {
      symbol: "Bs",
      exchange: bsExchange,
    },
  };

  return result;
};

export const currencyExchange = (
  amount: number,
  exchanges: CurrencyExchanges,
  to: Currency,
  from: Currency = "BS"
): number => {
  const exchange = exchanges[to].exchange / exchanges[from].exchange;
  return amount * exchange;
};

export const currencyExchangeText = (
  amount: number,
  exchanges: CurrencyExchanges,
  to: Currency,
  from: Currency = "BS"
): string => {
  let realAmount = currencyExchange(amount, exchanges, to, from);
  if (realAmount < 0.01 && realAmount > -0.01) {
    realAmount = 0;
  }
  const result = formatAmount(realAmount);

  const symbol = exchanges[to].symbol;

  return `${symbol} ${result}`;
};
